import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ApClassDTOBase } from "./ApClassDTOBase";
import type { ApClassFormatStaff } from "./ApClassFormatStaff";
import type { ApClassSessionStaff } from "./ApClassSessionStaff";
import type { ApClassSignupStaff } from "./ApClassSignupStaff";
import type { ApClassTypeStaff } from "./ApClassTypeStaff";
import type { ClassAvailability } from "./ClassAvailability";
import type { ClassLocationStaff } from "./ClassLocationStaff";
import type { PersonStaff } from "./PersonStaff";
import type { TrackedBase } from "./TrackedBase";

export class ApClassStaff implements ApClassDTOBase, TrackedBase {
  constructor(public readonly instanceId: number, public readonly cancelByOverride: Date | null, public readonly signupsStartOverride: Date | null, public readonly cancelledDatetime: Date | null, public readonly price: number | null, public readonly signupMax: number | null, public readonly signupMin: number | null, public readonly doNotAutoCancel: string | null, public readonly hideOnline: string | null, public readonly availability: ClassAvailability | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly formatId: ApClassFormatStaff | null, public readonly locationId: ClassLocationStaff | null, public readonly instructorId: PersonStaff | null, public readonly sessions: Array<ApClassSessionStaff> | null, public readonly signups: Array<ApClassSignupStaff> | null, public readonly typeId: ApClassTypeStaff | null) {
    this.cbiServiceContext = null;
    this.formatIdLoaded = this.formatId != null;
    this.formatIdCached = this.formatId;
    this.locationIdLoaded = this.locationId != null;
    this.locationIdCached = this.locationId;
    this.instructorIdLoaded = this.instructorId != null;
    this.instructorIdCached = this.instructorId;
    this.sessionsLoaded = this.sessions != null;
    this.sessionsCached = this.sessions;
    this.signupsLoaded = this.signups != null;
    this.signupsCached = this.signups;
    this.typeIdLoaded = this.typeId != null;
    this.typeIdCached = this.typeId;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private formatIdLoaded: boolean;
  private formatIdCached: ApClassFormatStaff | null;
  private locationIdLoaded: boolean;
  private locationIdCached: ClassLocationStaff | null;
  private instructorIdLoaded: boolean;
  private instructorIdCached: PersonStaff | null;
  private sessionsLoaded: boolean;
  private sessionsCached: Array<ApClassSessionStaff> | null;
  private signupsLoaded: boolean;
  private signupsCached: Array<ApClassSignupStaff> | null;
  private typeIdLoaded: boolean;
  private typeIdCached: ApClassTypeStaff | null;
  async formatIdLazy(): Promise<ApClassFormatStaff | null> {
    if (!this.formatIdLoaded) {
      this.formatIdCached = await this.cbiServiceContext!.apClassService().getApClassStaffFormatId(this.instanceId);
      this.formatIdLoaded = true;
    }
    return this.formatIdCached;
  }
  async locationIdLazy(): Promise<ClassLocationStaff | null> {
    if (!this.locationIdLoaded) {
      this.locationIdCached = await this.cbiServiceContext!.apClassService().getApClassStaffLocationId(this.instanceId);
      this.locationIdLoaded = true;
    }
    return this.locationIdCached;
  }
  async instructorIdLazy(): Promise<PersonStaff | null> {
    if (!this.instructorIdLoaded) {
      this.instructorIdCached = await this.cbiServiceContext!.apClassService().getApClassStaffInstructorId(this.instanceId);
      this.instructorIdLoaded = true;
    }
    return this.instructorIdCached;
  }
  async sessionsLazy(): Promise<Array<ApClassSessionStaff>> {
    if (!this.sessionsLoaded) {
      this.sessionsCached = await this.cbiServiceContext!.apClassService().getApClassStaffSessions(this.instanceId);
      this.sessionsLoaded = true;
    }
    return this.sessionsCached!;
  }
  async signupsLazy(): Promise<Array<ApClassSignupStaff>> {
    if (!this.signupsLoaded) {
      this.signupsCached = await this.cbiServiceContext!.apClassService().getApClassStaffSignups(this.instanceId);
      this.signupsLoaded = true;
    }
    return this.signupsCached!;
  }
  async typeIdLazy(): Promise<ApClassTypeStaff> {
    if (!this.typeIdLoaded) {
      this.typeIdCached = await this.cbiServiceContext!.apClassService().getApClassStaffTypeId(this.instanceId);
      this.typeIdLoaded = true;
    }
    return this.typeIdCached!;
  }
  async summaryLabel(): Promise<string> {
    return await this.cbiServiceContext!.apClassService().summaryLabelStaff({ instanceId: this.instanceId });
  }
}
